import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { vendorService } from '../../../services/api';
import DateRangePicker from '../../../components/DateRangePicker';
import {
  Building2,
  Layers,
  Users,
  User,
  Award,
  Crown,
  IndianRupee,
  Search,
  RotateCw,
  Eye,
  X,
  ExternalLink,
  ChevronDown,
  Filter,
  Phone,
  Mail,
  Calendar,
  Sparkles
} from 'lucide-react';

const formatINR = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages) {
    for (let i = 0; i < totalPages; i++) {
      pages.push(i);
    }
  } else {
    pages.push(0);
    let start = Math.max(1, currentPage - 1);
    let end = Math.min(totalPages - 2, currentPage + 1);

    if (currentPage <= 1) {
      end = 3;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 4;
    }

    if (start > 1) {
      pages.push('ellipsis1');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 2) {
      pages.push('ellipsis2');
    }

    pages.push(totalPages - 1);
  }
  return pages;
};

const CityCategoryDensityTab = () => {
  const { hasPermission } = useAuth();

  // Filter States
  const [minCount, setMinCount] = useState(15);
  const [selectedCategoryType, setSelectedCategoryType] = useState('ALL');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('APPROVED');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown options
  const [availableCities, setAvailableCities] = useState([]);
  const [categories, setCategories] = useState([]);

  // Data States
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    total_groups: 0,
    unique_cities_count: 0,
    unique_categories_count: 0,
    total_listings: 0,
    total_free_count: 0,
    total_pro_count: 0,
    total_premium_count: 0,
    total_pro_revenue: 0,
    total_premium_revenue: 0,
    total_revenue: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination State with session persistence
  const [currentPage, setCurrentPageState] = useState(() => {
    try {
      const saved = sessionStorage.getItem('admin_page_city_category_density');
      return saved ? Math.max(0, parseInt(saved, 10)) : 0;
    } catch (e) {
      return 0;
    }
  });

  const setCurrentPage = useCallback((valOrFn) => {
    setCurrentPageState(prev => {
      const next = typeof valOrFn === 'function' ? valOrFn(prev) : valOrFn;
      try {
        sessionStorage.setItem('admin_page_city_category_density', String(next));
      } catch (e) {}
      return next;
    });
  }, []);

  const [pageSize, setPageSize] = useState(25);

  // Drill-down Modal State
  const [drillDownModal, setDrillDownModal] = useState({
    isOpen: false,
    city: '',
    categoryId: null,
    categoryName: '',
    loading: false,
    vendors: []
  });

  // Load locations and categories for filters on mount
  useEffect(() => {
    const loadFilterMetadata = async () => {
      try {
        const [locRes, catRes] = await Promise.all([
          vendorService.getPublicLocations(),
          vendorService.getCategories(false, 'ALL')
        ]);
        if (locRes && locRes.success && locRes.data) {
          const locData = locRes.data.cities ? locRes.data : (locRes.data?.data || {});
          const citiesFlat = Array.from(new Set(Object.values(locData.cities || {}).flat())).sort();
          setAvailableCities(citiesFlat);
        }
        if (catRes && catRes.success && catRes.data) {
          const catList = Array.isArray(catRes.data)
            ? catRes.data
            : (Array.isArray(catRes.data?.data) ? catRes.data.data : []);
          setCategories(catList);
        }
      } catch (err) {
        console.error('Failed to load filter metadata:', err);
      }
    };
    loadFilterMetadata();
  }, []);

  // Filter categories list by selected category type for dropdown
  const filteredCategories = useMemo(() => {
    if (!selectedCategoryType || selectedCategoryType === 'ALL') return categories;
    return categories.filter((cat) => {
      const cType = (cat.categoryType || cat.category_type || 'COMPANY').toUpperCase();
      return cType === selectedCategoryType.toUpperCase();
    });
  }, [categories, selectedCategoryType]);

  // Fetch Report Data
  const fetchDensityReport = useCallback(async () => {
    if (!hasPermission('MANAGE_MEMBERSHIPS')) return;
    setLoading(true);
    setError('');
    try {
      const params = {
        min_count: minCount,
        status_filter: statusFilter
      };
      if (selectedCity) params.city = selectedCity;
      if (selectedCategoryId) params.category_id = selectedCategoryId;
      if (selectedCategoryType && selectedCategoryType !== 'ALL') params.category_type = selectedCategoryType;
      if (selectedPlan && selectedPlan !== 'ALL') params.plan = selectedPlan;
      if (dateRange.startDate) params.start_date = dateRange.startDate;
      if (dateRange.endDate) params.end_date = dateRange.endDate;

      const res = await vendorService.getCityCategoryDensityReport(params);
      if (res && res.success && res.data) {
        const reportRows = Array.isArray(res.data.data)
          ? res.data.data
          : (Array.isArray(res.data) ? res.data : []);

        const reportSummary = res.data.summary || res.summary || {
          total_groups: 0,
          unique_cities_count: 0,
          unique_categories_count: 0,
          total_listings: 0,
          total_free_count: 0,
          total_pro_count: 0,
          total_premium_count: 0,
          total_pro_revenue: 0,
          total_premium_revenue: 0,
          total_revenue: 0
        };

        setData(reportRows);
        setSummary(reportSummary);
      } else {
        setError(res?.error || 'Failed to fetch density report.');
        setData([]);
      }
    } catch (err) {
      console.error('Error fetching city-category density report:', err);
      setError('An error occurred while loading the report.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [hasPermission, minCount, selectedCategoryType, selectedCity, selectedCategoryId, selectedPlan, statusFilter, dateRange]);

  useEffect(() => {
    fetchDensityReport();
  }, [fetchDensityReport]);

  // Filtered & Sorted Rows (Always ordered by highest total listings first)
  const processedData = useMemo(() => {
    const rawList = Array.isArray(data)
      ? data
      : (Array.isArray(data?.data) ? data.data : []);
    let list = [...rawList];

    // Search query within current results
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (item) =>
          item.city?.toLowerCase().includes(q) ||
          item.category_name?.toLowerCase().includes(q) ||
          item.state?.toLowerCase().includes(q)
      );
    }

    // Always sort by highest total_listings first (descending), then total_revenue desc, then city asc
    list.sort((a, b) => {
      const diffListings = (Number(b.total_listings) || 0) - (Number(a.total_listings) || 0);
      if (diffListings !== 0) return diffListings;
      const diffRev = (Number(b.total_revenue) || 0) - (Number(a.total_revenue) || 0);
      if (diffRev !== 0) return diffRev;
      return (a.city || '').localeCompare(b.city || '');
    });

    return list;
  }, [data, searchQuery]);

  const totalPages = Math.ceil(processedData.length / pageSize) || 1;
  const safeCurrentPage = Math.min(currentPage, Math.max(0, totalPages - 1));

  // Paginated Rows
  const paginatedData = useMemo(() => {
    const start = safeCurrentPage * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, safeCurrentPage, pageSize]);

  // Reset Filters
  const handleResetFilters = () => {
    setMinCount(15);
    setSelectedCategoryType('ALL');
    setSelectedCity('');
    setSelectedCategoryId('');
    setSelectedPlan('ALL');
    setStatusFilter('APPROVED');
    setDateRange({ startDate: '', endDate: '' });
    setSearchQuery('');
    setCurrentPage(0);
  };

  // Open Drill-down Modal
  const handleOpenDrillDown = async (item) => {
    setDrillDownModal({
      isOpen: true,
      city: item.city,
      categoryId: item.category_id,
      categoryName: item.category_name,
      loading: true,
      vendors: []
    });

    try {
      const res = await vendorService.getCityCategoryVendors({
        city: item.city,
        category_id: item.category_id,
        category_type: selectedCategoryType,
        status_filter: statusFilter
      });
      if (res && res.success && res.data) {
        const vendorList = Array.isArray(res.data.vendors)
          ? res.data.vendors
          : (Array.isArray(res.vendors) ? res.vendors : []);
        setDrillDownModal((prev) => ({
          ...prev,
          loading: false,
          vendors: vendorList
        }));
      } else if (res && res.success && Array.isArray(res.vendors)) {
        setDrillDownModal((prev) => ({
          ...prev,
          loading: false,
          vendors: res.vendors
        }));
      } else {
        setDrillDownModal((prev) => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Error fetching cluster vendors:', err);
      setDrillDownModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseDrillDown = () => {
    setDrillDownModal({
      isOpen: false,
      city: '',
      categoryId: null,
      categoryName: '',
      loading: false,
      vendors: []
    });
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-gray-900">City-Category Density & Sales Analytics</h2>
            <span className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-emerald-200">
              High Density Clusters (&gt;= {minCount})
            </span>
          </div>
          <p className="text-xs text-gray-500">
            Identify market segments with concentrated listings, analyze membership conversions, and track segment sales revenue.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchDensityReport}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-semibold transition-all border border-gray-200 cursor-pointer disabled:opacity-50"
          >
            <RotateCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Top Dynamic Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Card 1: Unique Cities */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Unique Cities</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Building2 size={16} />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold text-gray-900">{summary.unique_cities_count || 0}</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">Cities meeting threshold</p>
          </div>
        </div>

        {/* Card 2: Unique Categories */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Categories</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Layers size={16} />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold text-gray-900">{summary.unique_categories_count || 0}</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">Active categories</p>
          </div>
        </div>

        {/* Card 3: Free Plan Listings */}
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Free Listings</span>
            <div className="w-8 h-8 rounded-lg bg-gray-100 text-gray-600 flex items-center justify-center">
              <Users size={16} />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold text-gray-900">{summary.total_free_count || 0}</h3>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {summary.total_listings > 0
                ? `${Math.round(((summary.total_free_count || 0) / summary.total_listings) * 100)}% of total`
                : 'Free tier'}
            </p>
          </div>
        </div>

        {/* Card 4: Professional Plan */}
        <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Professional</span>
            <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
              <Award size={16} />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold text-gray-900">{summary.total_pro_count || 0}</h3>
            <p className="text-[11px] font-semibold text-blue-600 mt-0.5">
              {formatINR(summary.total_pro_revenue || 0)}
            </p>
          </div>
        </div>

        {/* Card 5: Premium Plan */}
        <div className="bg-white p-5 rounded-2xl border border-amber-100 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Premium</span>
            <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
              <Crown size={16} />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-bold text-gray-900">{summary.total_premium_count || 0}</h3>
            <p className="text-[11px] font-semibold text-amber-600 mt-0.5">
              {formatINR(summary.total_premium_revenue || 0)}
            </p>
          </div>
        </div>

        {/* Card 6: Total Segment Revenue */}
        <div className="bg-gradient-to-br from-emerald-500 to-[#1f9477] p-5 rounded-2xl text-white shadow-md shadow-emerald-500/15 relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-emerald-100 uppercase tracking-wider">Segment Sales</span>
            <div className="w-8 h-8 rounded-lg bg-white/20 text-white flex items-center justify-center">
              <IndianRupee size={16} />
            </div>
          </div>
          <div className="mt-3">
            <h3 className="text-2xl font-black text-white">{formatINR(summary.total_revenue || 0)}</h3>
            <p className="text-[11px] text-emerald-100 mt-0.5">{summary.total_listings || 0} Total Listings</p>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Filter size={15} className="text-[#2ab391]" />
            <span className="text-sm font-bold text-gray-800">Filter &amp; Density Controls</span>
          </div>
          <button
            onClick={handleResetFilters}
            className="text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
          >
            Reset All
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3.5">
          {/* Dynamic Threshold Input */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600 flex items-center justify-between">
              <span>Min Listings</span>
              <span className="text-[10px] text-emerald-600 font-bold">&gt;= {minCount}</span>
            </label>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min="1"
                max="1000"
                value={minCount}
                onChange={(e) => setMinCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391]"
              />
            </div>
          </div>

          {/* Category Type Filter */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600">Category Type</label>
            <div className="relative">
              <select
                value={selectedCategoryType}
                onChange={(e) => {
                  setSelectedCategoryType(e.target.value);
                  setSelectedCategoryId(''); // Reset category on type change
                }}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391] appearance-none cursor-pointer"
              >
                <option value="ALL">All Types</option>
                <option value="COMPANY">Company Categories</option>
                <option value="INDIVIDUAL">Individual Categories</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* City Filter */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600">City</label>
            <div className="relative">
              <select
                value={selectedCity}
                onChange={(e) => setSelectedCity(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391] appearance-none cursor-pointer"
              >
                <option value="">All Cities</option>
                {availableCities.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Category Filter */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600">Category</label>
            <div className="relative">
              <select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391] appearance-none cursor-pointer"
              >
                <option value="">All Categories</option>
                {filteredCategories.map((cat) => {
                  const catType = (cat.categoryType || cat.category_type || 'COMPANY').toUpperCase();
                  const typeLabel = selectedCategoryType === 'ALL'
                    ? ` (${catType === 'INDIVIDUAL' ? 'Individual' : 'Company'})`
                    : '';
                  return (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}{typeLabel}
                    </option>
                  );
                })}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Plan Filter */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600">Plan Filter</label>
            <div className="relative">
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391] appearance-none cursor-pointer"
              >
                <option value="ALL">All Plans</option>
                <option value="FREE">Has Free Listings</option>
                <option value="PROFESSIONAL">Has Professional Listings</option>
                <option value="PREMIUM">Has Premium Listings</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Status Toggle */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600">Listing Status</label>
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#2ab391] appearance-none cursor-pointer"
              >
                <option value="APPROVED">Approved &amp; Live Only</option>
                <option value="ALL">All Listings</option>
              </select>
              <ChevronDown size={14} className="absolute right-3 top-2.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Date Range Picker */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-gray-600">Date Range</label>
            <DateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
              placeholder="All Time"
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-semibold text-gray-800 focus:outline-none"
            />
          </div>
        </div>

        {/* Quick Search in Results */}
        <div className="pt-2 flex items-center justify-between gap-4">
          <div className="relative w-full max-w-xs">
            <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search in results (City, Category)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-8 pr-3 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2ab391]"
            />
          </div>
          <span className="text-xs text-gray-500 font-medium">
            Showing <strong className="text-gray-800">{processedData.length}</strong> high-density segment{processedData.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs">
          {error}
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/75 border-b border-gray-100 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                <th className="py-3.5 px-4">#</th>
                <th className="py-3.5 px-4">City</th>
                <th className="py-3.5 px-4">Category</th>
                <th className="py-3.5 px-4 text-center">Free Plan</th>
                <th className="py-3.5 px-4 text-center">Professional Plan</th>
                <th className="py-3.5 px-4 text-center">Premium Plan</th>
                <th className="py-3.5 px-4 text-center">Total Listings</th>
                <th className="py-3.5 px-4 text-right">Total Revenue</th>
                <th className="py-3.5 px-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="9" className="py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RotateCw size={20} className="animate-spin text-[#2ab391]" />
                      <span>Loading density analytics...</span>
                    </div>
                  </td>
                </tr>
              ) : paginatedData.length > 0 ? (
                paginatedData.map((item, idx) => {
                  const globalIdx = safeCurrentPage * pageSize + idx + 1;
                  const paidPercentage = item.total_listings > 0
                    ? Math.round(((item.professional_count + item.premium_count) / item.total_listings) * 100)
                    : 0;

                  return (
                    <tr
                      key={`${item.city}-${item.category_id}`}
                      className="hover:bg-gray-50/75 transition-colors group"
                    >
                      <td className="py-3.5 px-4 font-bold text-gray-400">{globalIdx}</td>
                      
                      {/* City */}
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-gray-900">{item.city}</div>
                        {item.state && (
                          <span className="text-[10px] text-gray-400">{item.state}</span>
                        )}
                      </td>

                      {/* Category */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-800">{item.category_name}</span>
                          {item.category_type === 'INDIVIDUAL' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
                              <User size={10} />
                              Individual
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                              <Building2 size={10} />
                              Company
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Free Plan */}
                      <td className="py-3.5 px-4 text-center">
                        <span className="inline-block px-2.5 py-1 rounded-lg bg-gray-100 text-gray-700 font-bold text-xs">
                          {item.free_count}
                        </span>
                      </td>

                      {/* Professional Plan */}
                      <td className="py-3.5 px-4 text-center">
                        {item.professional_count > 0 ? (
                          <div className="inline-flex flex-col items-center">
                            <span className="px-2.5 py-0.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 font-bold text-xs">
                              {item.professional_count}
                            </span>
                            {item.professional_revenue > 0 && (
                              <span className="text-[10px] text-blue-600 font-medium mt-0.5">
                                {formatINR(item.professional_revenue)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300 font-bold">0</span>
                        )}
                      </td>

                      {/* Premium Plan */}
                      <td className="py-3.5 px-4 text-center">
                        {item.premium_count > 0 ? (
                          <div className="inline-flex flex-col items-center">
                            <span className="px-2.5 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 font-bold text-xs">
                              {item.premium_count}
                            </span>
                            {item.premium_revenue > 0 && (
                              <span className="text-[10px] text-amber-600 font-medium mt-0.5">
                                {formatINR(item.premium_revenue)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-300 font-bold">0</span>
                        )}
                      </td>

                      {/* Total Listings */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 rounded-lg text-emerald-800 font-black text-sm border border-emerald-100">
                          <Sparkles size={12} className="text-emerald-500" />
                          {item.total_listings}
                        </div>
                        {paidPercentage > 0 && (
                          <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">
                            {paidPercentage}% Paid
                          </div>
                        )}
                      </td>

                      {/* Total Revenue */}
                      <td className="py-3.5 px-4 text-right">
                        <span className="font-bold text-gray-900 text-sm">
                          {formatINR(item.total_revenue || 0)}
                        </span>
                      </td>

                      {/* Action */}
                      <td className="py-3.5 px-4 text-center">
                        <button
                          onClick={() => handleOpenDrillDown(item)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-[#2ab391] text-gray-700 hover:text-white transition-all text-xs font-semibold cursor-pointer"
                          title="View all vendors in this segment"
                        >
                          <Eye size={13} />
                          <span>View Vendors</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9" className="py-12 text-center text-gray-400">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Building2 size={28} className="text-gray-300" />
                      <p className="text-sm font-semibold text-gray-600">
                        No segments found with &gt;= {minCount} listings
                      </p>
                      <p className="text-xs text-gray-400 max-w-sm">
                        Try reducing the "Min Listings" threshold above (e.g. to 5 or 10) or clearing specific filters.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar (Matching Admin Dashboard Standard) */}
        <div className="p-4 border-t border-gray-150/80 bg-gray-50/50 flex flex-col sm:flex-row items-center justify-between gap-4 select-none">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => fetchDensityReport()}
              disabled={loading}
              title="Reload current page"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-600 hover:text-[#2ab391] hover:border-[#2ab391]/40 hover:bg-teal-50/50 rounded-xl font-bold text-xs transition-all cursor-pointer shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCw size={12} className={loading ? 'animate-spin' : ''} />
              Reload
            </button>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Show</span>
            <div className="relative">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0);
                }}
                className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={12} />
              </span>
            </div>
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
          </div>

          {totalPages > 0 && (
            <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                disabled={safeCurrentPage === 0}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center text-xs"
              >
                Previous
              </button>

              <div className="hidden sm:flex items-center gap-1">
                {getPageNumbers(safeCurrentPage, totalPages).map((pageNum, idx) => {
                  if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                    return (
                      <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold text-xs">
                        ...
                      </span>
                    );
                  }
                  const isActive = safeCurrentPage === pageNum;
                  return (
                    <button
                      key={`page-${pageNum}`}
                      type="button"
                      onClick={() => setCurrentPage(pageNum)}
                      className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${
                        isActive
                          ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                {safeCurrentPage + 1} / {totalPages}
              </span>

              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={safeCurrentPage >= totalPages - 1}
                className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center text-xs"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Drill-down Modal ("View Vendors") */}
      {drillDownModal.isOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-gray-900">
                    {drillDownModal.categoryName} in {drillDownModal.city}
                  </h3>
                  <span className="bg-[#2ab391]/10 text-[#2ab391] text-xs font-bold px-2.5 py-0.5 rounded-full">
                    {drillDownModal.vendors.length} Listings
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Complete list of vendor businesses in this cluster for sales and outreach.
                </p>
              </div>
              <button
                onClick={handleCloseDrillDown}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 overflow-y-auto flex-1 divide-y divide-gray-100">
              {drillDownModal.loading ? (
                <div className="py-12 text-center text-gray-400 flex flex-col items-center justify-center gap-2">
                  <RotateCw size={22} className="animate-spin text-[#2ab391]" />
                  <span>Loading vendor details...</span>
                </div>
              ) : drillDownModal.vendors.length > 0 ? (
                <div className="space-y-3">
                  {drillDownModal.vendors.map((vendor) => (
                    <div
                      key={vendor.id}
                      className="p-4 rounded-xl border border-gray-100 bg-white hover:border-gray-200 transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-2xs"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-bold text-gray-900 truncate">
                            {vendor.company_name}
                          </h4>
                          {/* Type Badge */}
                          {vendor.listing_type === 'INDIVIDUAL' || vendor.category_type === 'INDIVIDUAL' ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800">
                              👤 Individual
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-800">
                              🏢 Company
                            </span>
                          )}
                          {/* Plan Badge */}
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              vendor.plan_name === 'PREMIUM'
                                ? 'bg-amber-100 text-amber-800'
                                : vendor.plan_name === 'PROFESSIONAL'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {vendor.plan_name}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                          {vendor.phone && vendor.phone !== 'N/A' && (
                            <span className="flex items-center gap-1 font-medium text-gray-700">
                              <Phone size={12} className="text-[#2ab391]" />
                              {vendor.phone}
                            </span>
                          )}
                          {vendor.email && vendor.email !== 'N/A' && (
                            <span className="flex items-center gap-1 text-gray-500">
                              <Mail size={12} />
                              {vendor.email}
                            </span>
                          )}
                          {vendor.created_at && (
                            <span className="flex items-center gap-1 text-gray-400 text-[11px]">
                              <Calendar size={11} />
                              {new Date(vendor.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 self-end sm:self-center">
                        <a
                          href={`/listing/${vendor.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-[#2ab391] text-gray-700 hover:text-white transition-all text-xs font-semibold flex items-center gap-1"
                        >
                          <span>Listing Page</span>
                          <ExternalLink size={12} />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-gray-400">No vendor details found.</div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex justify-end">
              <button
                onClick={handleCloseDrillDown}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl text-xs font-bold transition-all cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CityCategoryDensityTab;
